import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Copy,
  Loader2,
  Plus,
  Save,
  Sparkles,
  Trash2,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  copyBudgetFrom,
  fetchBudget,
  fetchBudgetCategories,
  fetchBudgetPeriods,
  saveBudget,
} from "../lib/api";
import { normalizeDetail } from "../lib/errors";
import { numberToIndianWords } from "../lib/numbersInWords";

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const inr = (n) => (n == null ? "—" : `₹${INR.format(Math.round(Number(n) || 0))}`);

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function currentPeriodKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Clamp any period-key candidate to no later than the current month. Keeps future values
// (from bookmarks, hash edits, or old cached state) from ever landing us on a period the
// backend will refuse to save.
function clampToCurrent(key) {
  const cur = currentPeriodKey();
  if (!key) return cur;
  return key > cur ? cur : key;
}

function readablePeriod(key) {
  if (!key) return "";
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_LABELS[m - 1]} ${y}`;
}

function newLineFor(kind, group, label, categoryId = null) {
  return {
    line_id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    kind,
    group,
    category_id: categoryId,
    label,
    monthly_amount: 0,
    annual_amount: 0,
    note: "",
    source: "manual",
    source_ref: null,
  };
}

/**
 * BudgetPage — monthly budget planner. Users pick a month, edit line items, and save.
 * Insurance premiums + loan EMIs are auto-populated from active policies and liabilities.
 */
export function BudgetPage() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [periodKey, setPeriodKey] = useState(currentPeriodKey());
  const [budget, setBudget] = useState(null); // {items, totals, is_saved, ...}
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadBudget = useCallback(async (key) => {
    setLoading(true);
    try {
      const [b, cats] = await Promise.all([fetchBudget(key), fetchBudgetCategories()]);
      setBudget(b);
      setCategories(cats);
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load the budget."));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPeriods = useCallback(async () => {
    try {
      setPeriods(await fetchBudgetPeriods());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refreshPeriods();
  }, [refreshPeriods]);

  useEffect(() => {
    loadBudget(periodKey);
  }, [periodKey, loadBudget]);

  // ---------- Line-item mutations ----------

  function updateLine(lineId, patch) {
    setBudget((b) => {
      if (!b) return b;
      const items = b.items.map((it) => {
        if (it.line_id !== lineId) return it;
        const merged = { ...it, ...patch };
        // Keep annual = monthly × 12 unless the user manually edited annual afterward.
        if ("monthly_amount" in patch && !("annual_amount" in patch)) {
          merged.annual_amount = round2((Number(merged.monthly_amount) || 0) * 12);
        }
        return merged;
      });
      return { ...b, items, totals: computeTotals(items) };
    });
  }

  function removeLine(lineId) {
    setBudget((b) => {
      const items = b.items.filter((it) => it.line_id !== lineId);
      return { ...b, items, totals: computeTotals(items) };
    });
  }

  function addLine(kind, group, label, categoryId = null) {
    setBudget((b) => {
      const items = [...b.items, newLineFor(kind, group, label, categoryId)];
      return { ...b, items, totals: computeTotals(items) };
    });
  }

  // ---------- Save ----------

  async function handleSave() {
    if (!budget) return;
    setSaving(true);
    try {
      const saved = await saveBudget(periodKey, { items: budget.items });
      setBudget(saved);
      toast.success(`${readablePeriod(periodKey)} budget saved.`);
      refreshPeriods();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  }

  // ---------- Rollover ----------
  // The most recent saved period that isn't the currently open one — that's the natural
  // "copy from…" source. If everything the member has saved is the current month, hide
  // the button entirely (nothing to copy from).
  const copySource = useMemo(() => {
    if (!periods || !periods.length) return null;
    const past = periods.find((p) => p.period_key < periodKey);
    return past ? past.period_key : null;
  }, [periods, periodKey]);
  const [copying, setCopying] = useState(false);

  async function handleCopyFrom() {
    if (!copySource) return;
    // If the current month already has manual entries the user might have typed, warn
    // before we replace them — copy-from wipes manual lines and reseeds from source.
    const manualLines = (budget?.items || []).filter(
      (i) => i.source === "manual" && (Number(i.monthly_amount) || 0) > 0
    );
    if (manualLines.length > 0) {
      const ok = window.confirm(
        `This will replace ${manualLines.length} line item(s) in ` +
        `${readablePeriod(periodKey)} with the ones from ${readablePeriod(copySource)}. ` +
        `Insurance premiums and EMIs stay linked to your current policies / loans. Continue?`
      );
      if (!ok) return;
    }
    setCopying(true);
    try {
      const draft = await copyBudgetFrom(periodKey, copySource);
      setBudget(draft);
      toast.success(
        `Copied ${readablePeriod(copySource)} into ${readablePeriod(periodKey)}. ` +
          `Review and click Save.`
      );
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't copy the previous month."));
    } finally {
      setCopying(false);
    }
  }

  // ---------- Group helpers ----------

  const groupedItems = useMemo(() => {
    const groups = { income: {}, expense: {} };
    (budget?.items || []).forEach((it) => {
      const kind = it.kind === "income" ? "income" : "expense";
      groups[kind][it.group] = groups[kind][it.group] || [];
      groups[kind][it.group].push(it);
    });
    return groups;
  }, [budget]);

  // Category picker: for a given (kind, group), list categories that are ACTIVE.
  const categoriesByGroup = useMemo(() => {
    const map = { income: {}, expense: {} };
    categories.forEach((c) => {
      const k = c.kind;
      map[k][c.group] = map[k][c.group] || [];
      map[k][c.group].push(c);
    });
    return map;
  }, [categories]);

  // Groups the user should see even if empty (from taxonomy).
  const knownGroups = useMemo(() => {
    const income = new Set();
    const expense = new Set();
    categories.forEach((c) => (c.kind === "income" ? income : expense).add(c.group));
    // Include any groups the current budget has that aren't in taxonomy (user-added).
    (budget?.items || []).forEach((it) =>
      (it.kind === "income" ? income : expense).add(it.group),
    );
    return { income: [...income], expense: [...expense] };
  }, [categories, budget]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="budget-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs text-muted hover:text-gold"
        data-testid="page-back"
      >
        <ArrowLeft className="h-3 w-3" /> Go back
      </button>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold">Cashflow</div>
          <h1 className="mt-1 font-serif text-3xl text-text sm:text-4xl">Monthly Budget</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Plan income and expenses month-by-month. Insurance premiums and loan EMIs are
            auto-populated from your policies and liabilities — everything else is editable.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="period">Month</Label>
            <input
              id="period"
              type="month"
              value={periodKey}
              max={currentPeriodKey()}
              onChange={(e) => setPeriodKey(clampToCurrent(e.target.value))}
              className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
              data-testid="budget-period-picker"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !budget}
            data-testid="budget-save"
          >
            {saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Save {readablePeriod(periodKey)}</>
            )}
          </Button>
          {copySource && (
            <Button
              variant="outline"
              onClick={handleCopyFrom}
              disabled={copying || saving || loading}
              data-testid="budget-copy-from"
              title={`Copy every non-EMI, non-premium line from ${readablePeriod(copySource)} into this month.`}
            >
              {copying ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Copying…</>
              ) : (
                <><Copy className="mr-2 h-4 w-4" /> Copy {readablePeriod(copySource)}</>
              )}
            </Button>
          )}
        </div>
      </div>

      {loading || !budget ? (
        <div className="text-center text-sm text-muted">Loading…</div>
      ) : (
        <>
          <TotalsCard totals={budget.totals} periodKey={periodKey} isSaved={budget.is_saved} />

          <Section
            title="Income"
            kind="income"
            groups={knownGroups.income}
            groupedItems={groupedItems.income}
            categoriesByGroup={categoriesByGroup.income}
            onUpdate={updateLine}
            onRemove={removeLine}
            onAdd={addLine}
          />

          <Section
            title="Expenses"
            kind="expense"
            groups={knownGroups.expense}
            groupedItems={groupedItems.expense}
            categoriesByGroup={categoriesByGroup.expense}
            onUpdate={updateLine}
            onRemove={removeLine}
            onAdd={addLine}
          />

          {periods.length > 0 && (
            <PastPeriodsList periods={periods} onOpen={(k) => setPeriodKey(k)} activeKey={periodKey} />
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Totals card ---------- */

function TotalsCard({ totals, periodKey, isSaved }) {
  const savings = totals?.savings_monthly || 0;
  const savingsRate = totals?.income_monthly > 0
    ? Math.round((savings / totals.income_monthly) * 100)
    : null;
  return (
    <Card className="mb-6 border-gold/30" data-testid="budget-totals">
      <CardContent className="grid gap-4 p-6 sm:grid-cols-4">
        <Stat
          label={`Income (${readablePeriod(periodKey)})`}
          value={inr(totals?.income_monthly)}
          sub={`Annual ${inr(totals?.income_annual)}`}
          words={numberToIndianWords(totals?.income_monthly)}
          tone="text"
        />
        <Stat
          label="Expenses"
          value={inr(totals?.expense_monthly)}
          sub={`Annual ${inr(totals?.expense_annual)}`}
          words={numberToIndianWords(totals?.expense_monthly)}
          tone="text"
        />
        <Stat
          label="Monthly savings"
          value={inr(savings)}
          sub={
            savingsRate != null
              ? `${savingsRate}% of income`
              : "No income entered"
          }
          words={savings > 0 ? numberToIndianWords(savings) : ""}
          tone={savings >= 0 ? "positive" : "negative"}
        />
        <Stat
          label="Status"
          value={isSaved ? "Saved" : "Draft"}
          sub={isSaved ? "Persisted in your account" : "Click Save to persist"}
          tone={isSaved ? "positive" : "warn"}
        />
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, words, tone }) {
  const toneClass =
    tone === "positive" ? "text-emerald-300"
    : tone === "negative" ? "text-red-400"
    : tone === "warn" ? "text-amber-300"
    : "text-text";
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className={`font-serif text-2xl ${toneClass}`}>{value}</div>
      <div className="text-xs text-muted">{sub}</div>
      {words && (
        <div className="text-[10px] italic capitalize text-muted/80" data-testid="stat-words">
          {words}
        </div>
      )}
    </div>
  );
}

/* ---------- Section ---------- */

function Section({ title, kind, groups, groupedItems, categoriesByGroup, onUpdate, onRemove, onAdd }) {
  const Icon = kind === "income" ? TrendingUp : Wallet;
  return (
    <div className="mt-6" data-testid={`budget-section-${kind}`}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-gold" />
        <h2 className="font-serif text-xl text-text">{title}</h2>
      </div>
      <div className="flex flex-col gap-4">
        {groups.map((groupName) => (
          <GroupCard
            key={groupName}
            kind={kind}
            group={groupName}
            items={groupedItems[groupName] || []}
            categories={categoriesByGroup[groupName] || []}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onAdd={onAdd}
          />
        ))}
      </div>
    </div>
  );
}

function GroupCard({ kind, group, items, categories, onUpdate, onRemove, onAdd }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const subtotal = items.reduce((s, it) => s + (Number(it.monthly_amount) || 0), 0);
  const annualSubtotal = items.reduce((s, it) => s + (Number(it.annual_amount) || 0), 0);
  const usedCategoryIds = new Set(items.map((it) => it.category_id).filter(Boolean));
  const availableCategories = categories.filter((c) => !usedCategoryIds.has(c.id));
  return (
    <Card data-testid={`budget-group-${slug(group)}`}>
      <CardContent className="flex flex-col gap-3 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-text">{group}</h3>
          <div className="text-right">
            <div className="font-serif text-lg text-gold">{inr(subtotal)}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted">
              Annual {inr(annualSubtotal)}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="pb-2 text-left">Item</th>
                <th className="pb-2 text-right">Monthly ₹</th>
                <th className="pb-2 text-right">Annual ₹</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-xs text-muted">
                    No items yet — add one below.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <BudgetRow
                  key={it.line_id}
                  item={it}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Add-line picker */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen((s) => !s)}
            data-testid={`budget-add-${slug(group)}`}
          >
            <Plus className="mr-1.5 h-3 w-3" /> Add line
          </Button>
          {pickerOpen && (
            <div className="flex flex-wrap items-center gap-2">
              {availableCategories.length > 0 && (
                <select
                  className="h-9 rounded-md border border-border bg-elevated px-2 text-sm text-text"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const cat = availableCategories.find((c) => c.id === e.target.value);
                    if (cat) onAdd(kind, group, cat.name, cat.id);
                    e.target.value = "";
                    setPickerOpen(false);
                  }}
                  defaultValue=""
                  data-testid={`budget-picker-${slug(group)}`}
                >
                  <option value="">— pick a category —</option>
                  {availableCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
              <Input
                placeholder="…or type a custom name (e.g. Netflix)"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="h-9 w-56 text-sm"
                data-testid={`budget-custom-${slug(group)}`}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const nm = customName.trim();
                  if (!nm) {
                    toast.error("Give the item a name first.");
                    return;
                  }
                  onAdd(kind, group, nm, null);
                  setCustomName("");
                  setPickerOpen(false);
                }}
                data-testid={`budget-custom-add-${slug(group)}`}
              >
                Add custom
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function BudgetRow({ item, onUpdate, onRemove }) {
  const isAuto = item.source === "policy_auto" || item.source === "liability_auto";
  return (
    <tr className="hover:bg-elevated/30" data-testid={`budget-row-${item.line_id}`}>
      <td className="py-2 pr-2">
        <div className="text-text">{item.label}</div>
        {isAuto && (
          <div className="text-[10px] uppercase tracking-widest text-gold/70">
            Auto · {item.source === "policy_auto" ? "from policy" : "from liability"}
          </div>
        )}
      </td>
      <td className="py-2 pr-2 text-right">
        <input
          type="number"
          value={item.monthly_amount || ""}
          onChange={(e) =>
            onUpdate(item.line_id, {
              monthly_amount: Number(e.target.value) || 0,
            })
          }
          className="w-28 rounded-md border border-border bg-elevated px-2 py-1 text-right font-mono text-sm text-text focus:border-gold focus:outline-none"
          placeholder="0"
          min="0"
          step="500"
          data-testid={`budget-input-monthly-${item.line_id}`}
        />
        {item.monthly_amount > 0 && (
          <div className="mt-1 text-right text-[10px] italic text-muted" data-testid={`budget-words-monthly-${item.line_id}`}>
            {numberToIndianWords(item.monthly_amount)}
          </div>
        )}
      </td>
      <td className="py-2 pr-2 text-right">
        <input
          type="number"
          value={item.annual_amount || ""}
          onChange={(e) =>
            onUpdate(item.line_id, {
              annual_amount: Number(e.target.value) || 0,
            })
          }
          className="w-32 rounded-md border border-border bg-elevated px-2 py-1 text-right font-mono text-sm text-text focus:border-gold focus:outline-none"
          placeholder="0"
          min="0"
          step="500"
          data-testid={`budget-input-annual-${item.line_id}`}
        />
        {item.annual_amount > 0 && (
          <div className="mt-1 text-right text-[10px] italic text-muted" data-testid={`budget-words-annual-${item.line_id}`}>
            {numberToIndianWords(item.annual_amount)}
          </div>
        )}
      </td>
      <td className="py-2 text-right">
        <button
          type="button"
          onClick={() => onRemove(item.line_id)}
          className="rounded-md p-1 text-muted hover:text-red-400"
          aria-label="Remove"
          data-testid={`budget-remove-${item.line_id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

/* ---------- Past periods ---------- */

function PastPeriodsList({ periods, onOpen, activeKey }) {
  return (
    <div className="mt-8" data-testid="budget-past-list">
      <h3 className="font-serif text-lg text-text">Past months</h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {periods.map((p) => {
          const savings = p.totals?.savings_monthly || 0;
          const positive = savings >= 0;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpen(p.period_key)}
              className={`flex items-center justify-between gap-3 rounded-lg border p-3 text-left ${
                p.period_key === activeKey
                  ? "border-gold bg-gold/5"
                  : "border-border bg-card hover:border-gold/40"
              }`}
              data-testid={`budget-past-${p.period_key}`}
            >
              <div>
                <div className="text-sm text-text">{readablePeriod(p.period_key)}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted">
                  {p.items_count} line items
                </div>
              </div>
              <div className="text-right">
                <div className={`font-serif text-lg ${positive ? "text-emerald-300" : "text-red-400"}`}>
                  {positive ? "+" : ""}{inr(savings)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted">
                  Savings
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function computeTotals(items) {
  let im = 0, ex = 0, ia = 0, ea = 0;
  items.forEach((it) => {
    const m = Number(it.monthly_amount) || 0;
    const a = Number(it.annual_amount) || m * 12;
    if (it.kind === "income") { im += m; ia += a; }
    else { ex += m; ea += a; }
  });
  return {
    income_monthly: round2(im),
    expense_monthly: round2(ex),
    savings_monthly: round2(im - ex),
    income_annual: round2(ia),
    expense_annual: round2(ea),
    savings_annual: round2(ia - ea),
  };
}

export default BudgetPage;
